/**
 * Target environments for the internal tool's GraphQL client. Although this
 * module lives under crown-data-import, `apollo-client.ts` resolves its backend
 * URL from here, so the selected env drives every GraphQL-backed surface in the
 * tool (schema editor, logs, statistics, imports), not just Crown imports.
 *
 * It does NOT cover REST calls that build their own base URL. `utils/queue-api.ts`
 * reads the fixed `REACT_APP_BACKEND_URL`, so the Queues page always talks to the
 * deployment's own backend regardless of the selection here. Anything added that
 * bypasses the Apollo client needs routing through `getBackendUrlForEnv` to
 * follow the picker.
 *
 * The tool is deployed per environment (dev/demo/staging/release/prod), and each
 * deployment can also target any other env's backend via the picker.
 */
export const CROWN_ENVS = [
  "dev",
  "demo",
  "staging",
  "release",
  "prod",
] as const;

export type CrownEnv = (typeof CROWN_ENVS)[number];

const CROWN_ENV_SET: ReadonlySet<string> = new Set(CROWN_ENVS);

const isCrownEnv = (value: string | null): value is CrownEnv =>
  value !== null && CROWN_ENV_SET.has(value);

/**
 * Reads the build-time `REACT_APP_BACKEND_URL_<ENV>` var for every env into a
 * map. Kept as a function (not a const) so it re-reads `process.env` on each
 * call, which is what lets tests toggle the vars at runtime; in a real build the
 * vars are inlined at build time and never change.
 */
const backendUrlByEnv = (): Record<CrownEnv, string | undefined> => ({
  dev: process.env.REACT_APP_BACKEND_URL_DEV,
  demo: process.env.REACT_APP_BACKEND_URL_DEMO,
  staging: process.env.REACT_APP_BACKEND_URL_STAGING,
  release: process.env.REACT_APP_BACKEND_URL_RELEASE,
  prod: process.env.REACT_APP_BACKEND_URL_PROD,
});

/**
 * Resolves the backend base URL for a target env from build-time env vars.
 *
 * Reads only the selected env's `REACT_APP_BACKEND_URL_<ENV>` var; there is no
 * cross-env fallback, so a missing var fails loudly rather than silently
 * targeting a different backend than the banner advertises.
 *
 * @param env - The selected target environment.
 * @returns The backend base URL for that env.
 * @throws Error when the selected env's URL var is unset or empty.
 */
export const getBackendUrlForEnv = (env: CrownEnv): string => {
  const url = backendUrlByEnv()[env];
  if (url === undefined || url === "") {
    throw new Error(
      `No backend URL configured for env "${env}". Set REACT_APP_BACKEND_URL_${env.toUpperCase()}.`,
    );
  }
  return url;
};

/**
 * Whether the given env has a non-empty backend URL configured for this build.
 */
const isEnvConfigured = (env: CrownEnv): boolean => {
  const url = backendUrlByEnv()[env];
  return url !== undefined && url !== "";
};

/**
 * The envs the picker may offer: only those whose backend URL is configured.
 *
 * Selecting an env whose `REACT_APP_BACKEND_URL_<ENV>` is unset would persist
 * that env to localStorage, and `apollo-client.ts` calls `getBackendUrlForEnv`
 * at module-import time, which throws for an unset URL. That white-screens the
 * app on the next reload with no in-app recovery. Filtering to configured envs
 * keeps the picker from ever offering an env that would crash the client.
 */
export const getSelectableEnvs = (): CrownEnv[] =>
  CROWN_ENVS.filter((env) => isEnvConfigured(env));

export const CROWN_ENV_STORAGE_KEY = "crownTargetEnv";

/**
 * Maps the build-time `REACT_APP_APP_ENV` (long and short spellings) to a Crown
 * target env.
 */
const APP_ENV_TO_CROWN_ENV = new Map<string, CrownEnv>([
  ["dev", "dev"],
  ["development", "dev"],
  ["demo", "demo"],
  ["staging", "staging"],
  ["release", "release"],
  ["prod", "prod"],
  ["production", "prod"],
]);

/**
 * Resolves the Crown env this build was deployed to, from the build-time
 * `REACT_APP_APP_ENV` (the same value that tiers Statsig in this app).
 *
 * Lets each internal deployment default to its OWN backend — the dev deployment
 * to dev, the prod deployment to prod. Unknown or unset values resolve to
 * "prod", the primary backend, so a misconfigured build fails safe toward
 * production rather than dev.
 */
const defaultEnvFromBuild = (): CrownEnv => {
  const appEnv = (process.env.REACT_APP_APP_ENV ?? "").trim().toLowerCase();
  return APP_ENV_TO_CROWN_ENV.get(appEnv) ?? "prod";
};

/**
 * Reads the persisted target env from localStorage, falling back to the env this
 * build was deployed to (see `defaultEnvFromBuild`).
 *
 * Absent an explicit selection the whole tool (schema editor, logs, statistics)
 * reads the data of the env it runs in, instead of a hardcoded env. Switching to
 * a different env is an explicit, persisted opt-in via the picker.
 *
 * Skips any env whose backend URL is unset, preferring the build's own env and
 * then any configured env. `apollo-client.ts` resolves the URL at module-import
 * time, so returning an unconfigured env throws before React mounts: the picker
 * never renders, and it is the only UI that could change the selection back.
 * That leaves no in-app recovery, and a fresh user cannot even clear
 * localStorage to escape, because the build default is recomputed every load.
 * `getSelectableEnvs` already keeps the picker from offering such an env; this
 * covers the read path, where an env selected while configured can later have
 * its var removed or rotated.
 *
 * @returns A configured env where one exists, otherwise the build's env.
 */
export const readPersistedEnv = (): CrownEnv => {
  const raw = localStorage.getItem(CROWN_ENV_STORAGE_KEY);
  if (isCrownEnv(raw) && isEnvConfigured(raw)) {
    return raw;
  }
  const buildEnv = defaultEnvFromBuild();
  if (isEnvConfigured(buildEnv)) {
    return buildEnv;
  }
  const [firstConfigured] = getSelectableEnvs();
  return firstConfigured ?? buildEnv;
};
